/**
 * Patient Card Component
 *
 * Displays patient summary for the Patient Database grid.
 * Shows internal ID, name, document count, history log status, and last activity.
 */

import { ChevronRight, FileText, Stethoscope, Pill, Activity, AlertTriangle, GitMerge } from 'lucide-react';
import type { PatientWithStats, HistoryLogStatus } from '../types';

interface PatientCardProps {
  patient: PatientWithStats;
  onClick?: () => void;
}

const STATUS_CONFIG: Record<HistoryLogStatus, { label: string; bg: string; text: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  complete: { label: 'Complete', bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
};

export function PatientCard({ patient, onClick }: PatientCardProps) {
  const lastActivity = patient.last_activity_at
    ? new Date(patient.last_activity_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const statusConfig = patient.history_log_status
    ? STATUS_CONFIG[patient.history_log_status]
    : null;

  const hasPendingActions = (patient.pending_merge_count > 0) || (patient.unresolved_discrepancy_count > 0);

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 transition-colors">
            {patient.first_name && patient.last_name
              ? `${patient.first_name} ${patient.last_name}`
              : patient.name || patient.internal_id}
          </h3>
          {patient.dob && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              DOB: {new Date(patient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
          {patient.mrn && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">MRN: {patient.mrn}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {statusConfig && (
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
              {statusConfig.label}
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400 mb-3">
        <div className="flex items-center gap-1" title="Documents">
          <FileText size={14} />
          <span>{patient.document_count}</span>
        </div>
        <div className="flex items-center gap-1" title="Diagnoses">
          <Stethoscope size={14} />
          <span>{patient.diagnosis_count}</span>
        </div>
        <div className="flex items-center gap-1" title="Medications">
          <Pill size={14} />
          <span>{patient.medication_count}</span>
        </div>
        <div className="flex items-center gap-1" title="Procedures">
          <Activity size={14} />
          <span>{patient.procedure_count}</span>
        </div>
      </div>

      {/* Pending actions */}
      {hasPendingActions && (
        <div className="flex flex-wrap gap-2 mb-3">
          {patient.pending_merge_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
              <GitMerge size={12} />
              {patient.pending_merge_count} merge{patient.pending_merge_count !== 1 ? 's' : ''}
            </span>
          )}
          {patient.unresolved_discrepancy_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
              <AlertTriangle size={12} />
              {patient.unresolved_discrepancy_count} discrepanc{patient.unresolved_discrepancy_count !== 1 ? 'ies' : 'y'}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {lastActivity ? `Active ${lastActivity}` : `Created ${new Date(patient.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        </p>
        <ChevronRight size={18} className="text-gray-400 dark:text-gray-500 group-hover:text-primary-600 transition-colors" />
      </div>
    </div>
  );
}
